import React, {useState, useEffect} from 'react'
import {Card, Form, Container} from 'react-bootstrap'
import {FiSettings, FiInfo, FiCheck} from 'react-icons/fi'
import {updateSetting, getSetting} from '../../api/settings.api'

// Setting keys
const LATEST_ALERTS_ONLY_KEY = 'bp_latest_alert_only'

const Settings: React.FC = () => {
  const [latestOnly, setLatestOnly] = useState<boolean>(false)
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // Load settings from API on mount
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true)
      try {
        // Load latest alerts setting
        const latestAlertsResponse = await getSetting(LATEST_ALERTS_ONLY_KEY)
        if (latestAlertsResponse?.data) {
          const apiValue =
            latestAlertsResponse.data.value === true || latestAlertsResponse.data.value === 'true'
          setLatestOnly(apiValue)
          // Sync localStorage with API value
          localStorage.setItem(LATEST_ALERTS_ONLY_KEY, apiValue.toString())
        } else {
          // Fallback to localStorage if API doesn't have the setting
          const localValue = localStorage.getItem(LATEST_ALERTS_ONLY_KEY)
          const fallbackValue = localValue !== 'false' // Default to true unless explicitly set to false
          setLatestOnly(fallbackValue)
        }

      } catch (error) {
        console.error('Error loading settings:', error)
        // Fallback to localStorage on API error
        const localValue = localStorage.getItem(LATEST_ALERTS_ONLY_KEY)
        const fallbackValue = localValue !== 'false' // Default to true unless explicitly set to false
        setLatestOnly(fallbackValue)
      } finally {
        setIsLoading(false)
      }
    }

    loadSettings()
  }, [])

  // Save settings to API when changed
  const handleToggle = () => {
    const newValue = !latestOnly
    setLatestOnly(newValue)
    setShowConfirmation(true)
    setTimeout(() => setShowConfirmation(false), 2000)

    // Save to localStorage for immediate use by other components
    localStorage.setItem(LATEST_ALERTS_ONLY_KEY, newValue.toString())

    // Save to API
    updateSetting(LATEST_ALERTS_ONLY_KEY, newValue.toString(), {
      description: 'Show only the latest alert per patient per category',
    })
  }

  return (
    <Container className='py-5'>
      <div className='d-flex align-items-center mb-4'>
        <FiSettings size={28} className='text-primary me-2' />
        <h2 className='mb-0'>Application Settings</h2>
      </div>

      <Card className='shadow-sm border-0 rounded-4 mb-4'>
        <Card.Header className='bg-white py-3'>
          <h5 className='mb-0'>Notification Preferences</h5>
        </Card.Header>
        <Card.Body className='p-4 '>
          <Form>
            <div className='d-flex align-items-start'>
              <div className='flex-grow-1 me-3'>
                <Form.Label className='fw-medium mb-1'>Latest Alerts Only</Form.Label>
                <p className='text-muted mb-3'>
                  <FiInfo className='me-1' size={14} />
                  When enabled, only the most recent blood pressure alert per patient per category
                  will be shown. Disable to see all historical alerts.
                </p>
                <div className='d-flex align-items-center'>
                  <Form.Check
                    type='switch'
                    id='latest-alert-toggle'
                    checked={latestOnly}
                    onChange={handleToggle}
                    className='me-2'
                    disabled={isLoading}
                  />
                  <span className={latestOnly ? 'text-success fw-medium' : 'text-muted'}>
                    {latestOnly ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
              <div className='bg-light rounded p-3'>
                <div className='text-center mb-2'>
                  <div
                    className='bg-white border rounded-circle d-inline-flex align-items-center justify-content-center'
                    style={{width: '48px', height: '48px'}}
                  >
                    <FiInfo className='text-primary' size={20} />
                  </div>
                </div>
                <p className='small text-muted mb-0'>
                  This setting affects how alerts are displayed in the dashboard and reports.
                </p>
              </div>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {showConfirmation && (
        <div className='position-fixed bottom-0 end-0 m-4'>
          <div className='d-flex align-items-center bg-success text-white p-3 rounded shadow'>
            <FiCheck className='me-2' size={20} />
            <span>Setting updated successfully!</span>
          </div>
        </div>
      )}
    </Container>
  )
}

export default Settings
